import React, { createContext, useContext, Dispatch, SetStateAction, useState } from "react";

type VolunteerMenuOptionType = {
    id: number;
    amount: number;
    text: string;
    category?: string;
};

type VolunteersSideMenuContext = {
    menuOptions: VolunteerMenuOptionType[];
    setMenuOptions: Dispatch<SetStateAction<VolunteerMenuOptionType[]>>;
    selectedMenuOptions: VolunteerMenuOptionType[];
    setSelectedMenuOptions: Dispatch<SetStateAction<VolunteerMenuOptionType[]>>;
    activeCount: number;
    setActiveCount: Dispatch<SetStateAction<number>>;
    inactiveCount: number;
    setInactiveCount: Dispatch<SetStateAction<number>>;
    refreshSideMenu: Function;
    refreshMenu: boolean;
    previousMenuOptions: VolunteerMenuOptionType[];
    setPreviousMenuOptions: Dispatch<SetStateAction<VolunteerMenuOptionType[]>>;
    searchText: any[] | string;
    setSearchText: Dispatch<SetStateAction<[] | string>>;
    inactiveSelected: boolean;
    setInactiveSelected: Dispatch<SetStateAction<boolean>>;
    bgcScoreFilter: { label: string; value: string };
    setBgcScoreFilter: Dispatch<SetStateAction<{ label: string; value: string }>>;
    eventFilter: { label: string; value: string };
    setEventFilter: Dispatch<SetStateAction<{ label: string; value: string }>>;
    prevBgcScoreFilter: any;
    setPrevBgcScoreFilter: any;
};

const VolunteersSideMenuContext = createContext<VolunteersSideMenuContext>({
    menuOptions: null,
    setMenuOptions: null,
    selectedMenuOptions: null,
    setSelectedMenuOptions: null,
    activeCount: null,
    setActiveCount: null,
    inactiveCount: null,
    setInactiveCount: null,
    refreshSideMenu: null,
    refreshMenu: null,
    previousMenuOptions: null,
    setPreviousMenuOptions: null,
    searchText: null,
    setSearchText: null,
    inactiveSelected: null,
    setInactiveSelected: null,
    bgcScoreFilter: null,
    setBgcScoreFilter: null,
    eventFilter: null,
    setEventFilter: null,
    prevBgcScoreFilter: null,
    setPrevBgcScoreFilter: null,
});

const activeOptions = [
    {
        id: 2,
        text: "Consent Pending",
        amount: 0,
        category: null,
    },
    {
        id: 5,
        text: "Disclosures/Review Consent",
        amount: 0,
        category: "DISCLOSURE",
    },
    {
        id: 6,
        text: "Reviewed But Not Submitted",
        amount: 0,
        category: null,
    },
    {
        id: 7,
        text: "In Progress",
        amount: 0,
        category: null,
    },
    {
        id: 8,
        text: "Findings",
        amount: 0,
        category: "BGC",
    },
    {
        id: 9,
        text: "Errors",
        amount: 0,
        category: null,
    },
    {
        id: 12,
        text: "Complete",
        amount: 0,
        category: null,
    },
];

const VolunteersSideMenuContextProvider: React.FC = ({ children }) => {
    const [menuOptions, setMenuOptions] = useState<VolunteerMenuOptionType[]>(activeOptions);
    const [selectedMenuOptions, setSelectedMenuOptions] = useState<VolunteerMenuOptionType[]>([]);
    const [previousMenuOptions, setPreviousMenuOptions] = useState<VolunteerMenuOptionType[]>([]);
    const [activeCount, setActiveCount] = useState<number>(0);
    const [inactiveCount, setInactiveCount] = useState<number>(0);
    const [refreshMenu, setRefreshMenu] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<Array<any> | string>([]);
    const [inactiveSelected, setInactiveSelected] = useState(false);
    const [bgcScoreFilter, setBgcScoreFilter] = useState<{ label: string; value: string }>({
        label: "All Volunteers",
        value: "All Volunteers",
    });
    const [eventFilter, setEventFilter] = useState<{ label: string; value: string }>({
        label: "All Events",
        value: "All Events",
    });
    const [prevBgcScoreFilter, setPrevBgcScoreFilter] = useState(null);

    const refreshSideMenu = () => {
        setRefreshMenu(true);
        setSelectedMenuOptions([...selectedMenuOptions]);
        setRefreshMenu(false);
    };

    return (
        <VolunteersSideMenuContext.Provider
            value={{
                menuOptions,
                setMenuOptions,
                selectedMenuOptions,
                setSelectedMenuOptions,
                previousMenuOptions,
                setPreviousMenuOptions,
                activeCount,
                setActiveCount,
                inactiveCount,
                setInactiveCount,
                refreshMenu,
                searchText,
                setSearchText,
                inactiveSelected,
                setInactiveSelected,
                refreshSideMenu,
                bgcScoreFilter,
                setBgcScoreFilter,
                eventFilter,
                setEventFilter,
                prevBgcScoreFilter,
                setPrevBgcScoreFilter,
            }}
        >
            {children}
        </VolunteersSideMenuContext.Provider>
    );
};

const useVolunteersSideMenu = (): VolunteersSideMenuContext => {
    const adminSideMenuContext = useContext<VolunteersSideMenuContext>(VolunteersSideMenuContext);
    if (adminSideMenuContext === undefined) {
        throw new Error(`useVolunteersSideMenu must be used within a AdminSideMenuProvider`);
    }
    return adminSideMenuContext;
};

export {
    VolunteersSideMenuContext,
    VolunteersSideMenuContextProvider,
    VolunteerMenuOptionType,
    useVolunteersSideMenu,
};
